import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchStaffList} from 'api';
import {createAddress, getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {IS_NG} from 'core/constants/env';
import {profile$, profileState$} from 'models/auth';
import {createParticipantFx, data$, loadParticipantsDataFx} from 'models/profile/common';
import {ParticipantRegistrationRequest, UserPermission} from 'types';
import {Address} from 'types/geo';
import {StaffListRawRow} from 'types/profile';
import {formatAddressInfoToRequest} from 'utils/address';
import {mapStorageTypesToI18nOptions} from 'utils/select';

import {CredentialsFIRSForm} from './FIRS';
import {
    Actions,
    AddButton,
    BackButton,
    CustomContentContainer,
    CustomTitle,
    Field,
    FormFields,
    Heading,
    SubmitButton,
} from './styled';

type FormField = {
    name: keyof ParticipantRegistrationRequest;
    readonly?: boolean;
    options?: SelectOption<string | number>[];
};

export const CommonData = () => {
    const {t, i18n} = useTranslation();

    const [collapsed, setCollapsed] = useState(false);

    const profile = useStore(profile$);
    const common = useStore(data$);
    const state = useStore(profileState$);

    const fieldsConfig = useMemo<Record<string, any>>(
        () => ({
            fullName: state.profile?.legalPerson?.company_name || '—',
            shortName: state.profile?.legalPerson?.short_name || '—',
            tin: state.profile?.legalPerson?.tin || '—',
            legalAddress: state.profile?.legalPerson?.legal_address || '—',
            ceo: state.profile?.fullName || '—',
            type: common?.type ? t(`dictionaries.profileRole.${common.type}`) : '—',
            postalCode: state.profile?.legalPerson?.postal_code || '—',
            gcp: state.profile?.client?.gcp || '—',
        }),
        [state.profile, common, t],
    );

    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );

    const translateParticipantForm = useCallback(
        (key: string) => t('pages.profile.participant.fields.' + key),
        [i18n.language],
    );

    useEffect(() => {
        if (profile) {
            setValue('first_name', profile?.firstName);
            setValue('last_name', profile?.lastName);
            setValue('role', profile?.client?.role);
            setValue('email', profile?.email);
        } else reset();
    }, [profile]);

    const participantFormSchema = yup.object().shape({
        storageName: yup.string().required(translateValidates('required')),
        storageGln: yup.string().notRequired(),
        storageTypeId: yup
            .number()
            .typeError(translateValidates('required'))
            .required(translateValidates('required')),
        userIds: yup
            .array()
            .typeError(translateValidates('required'))
            .of(yup.number().required(translateValidates('required')))
            .min(1, translateValidates('required')),
        storageAddress: yup.string().required(translateValidates('required')),
    });

    const {
        control,
        formState: {errors},
        handleSubmit,
        reset,
        setValue,
        setError,
    } = useForm<ParticipantRegistrationRequest>({
        resolver: yupResolver(participantFormSchema),
        mode: 'all',
    });

    const onSubmit = async (data: ParticipantRegistrationRequest) => {
        const {storageAddress, ...payload} = data;
        const storageAddressId = await getAddressId(storageAddress);
        if (!storageAddressId) throw Error('storageAddressId is required');
        payload.storageAddressId = storageAddressId;

        try {
            await createParticipantFx(payload);
            reset();
            loadParticipantsDataFx();
            setCollapsed(false);
        } catch (error) {
            console.error(error);
            const responseData = error.fullErrorsList;
            if (!Array.isArray(responseData)) return;

            responseData.forEach(({field, message}) => setError(field, {type: 'manual', message}));
        }
    };

    const [users, setUsers] = useState<StaffListRawRow[]>([]);
    const getUsers = async () => {
        try {
            const response = await fetchStaffList();
            setUsers(response);
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        if (collapsed) getUsers();
    }, [collapsed]);
    const userOptions = useMemo<SelectOption<number | null>[]>(() => {
        try {
            return users
                .filter((item) => !item.clientId)
                .map((item) => ({
                    value: item.id,
                    label: `${item.firstName} ${item.middleName || ''} ${item.lastName}`.trim(),
                }));
        } catch (error) {
            console.error(error);
            return [{value: null, label: ''}];
        }
    }, [users]);

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const getAddressId = async (address?: string) => {
        if (!address) return;
        try {
            const addressPayload = (partialAddress: string): Address => {
                const lang =
                    (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) ||
                    'ru';
                return {
                    address: partialAddress,
                    lang,
                };
            };

            const payload = addressPayload(address);
            return await getAddressInfoFromString(payload)
                .then(formatAddressInfoToRequest)
                .then(createAddress);
        } catch (error) {
            console.error(error);
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'storageName'},
            {name: 'storageGln'},
            {
                name: 'storageTypeId',
                options: mapStorageTypesToI18nOptions(
                    new Array(12).fill(0).map((_, i) => ++i),
                    'dictionaries.storageType',
                ),
            },
            {name: 'userIds'},
            {name: 'storageAddress'},
        ],
        [t, collapsed],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<ParticipantRegistrationRequest>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateParticipantForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        const props = {...baseProps, ...controlEvents};

        if (field.name === 'storageTypeId') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <Select
                        {...baseProps}
                        value={field.options?.find((o) => o.value === controlEvents.value) ?? null}
                        options={field.options}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                    />
                </div>
            );
        }

        if (field.name === 'userIds') {
            const selectedOptions = userOptions.filter((o) =>
                controlEvents.value.includes(o.value),
            );

            return (
                <div style={{paddingBottom: '6px'}}>
                    <Select
                        menuPlacement="top"
                        isMulti
                        value={selectedOptions}
                        options={userOptions}
                        {...baseProps}
                        onChange={(selected: SelectOption<number>[]) => {
                            const values = selected.map((o) => o.value);
                            controlEvents.onChange(values);
                        }}
                    />
                </div>
            );
        }

        if (field.name === 'storageAddress') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <AsyncSelect
                        {...baseProps}
                        menuPlacement="top"
                        name={controlEvents.name}
                        loadOptions={addressOptions}
                        isClearable
                        onChange={(v?: {value: string; label: string}) => {
                            controlEvents.onChange(v?.value || '');
                        }}
                        value={
                            controlEvents.value
                                ? {value: controlEvents.value, label: controlEvents.value}
                                : null
                        }
                    />
                </div>
            );
        }

        return <Input {...props} />;
    };

    const AddParticipantButton = () => {
        if (!state.isDirector) return null;
        return (
            <PermissionAccess permissions={[UserPermission.PROFILE_DATA_EDITING]}>
                <AddButton onClick={() => setCollapsed(true)}>
                    + {t('pages.profile.common.create')}
                </AddButton>
            </PermissionAccess>
        );
    };

    return (
        <CustomContentContainer status={state.loading ? 'loading' : 'default'}>
            <Heading>{t('pages.profile.pills.CommonData')}</Heading>
            <form>
                <FormFields>
                    {Object.entries(fieldsConfig).map(([key, value]) => (
                        <Field key={key}>
                            <span>{t(`pages.profile.common.${key}`)}</span>
                            <Input
                                readOnly
                                placeholder={t(`pages.profile.common.${key}`)}
                                formFieldName={key}
                                value={value}
                            />
                        </Field>
                    ))}
                </FormFields>
            </form>
            {collapsed ? (
                <form
                    onSubmit={handleSubmit(onSubmit, (err) => console.error(err))}
                    style={{marginTop: 50}}
                >
                    <CustomTitle size={4}>{t('pages.profile.common.create')}</CustomTitle>
                    <FormFields>
                        {formFields.map((field) => (
                            <Field
                                key={field.name}
                                text={(errors?.[field.name] as FieldError)?.message}
                                isError={!!errors?.[field.name]}
                            >
                                <span>{translateParticipantForm(field.name)}</span>
                                <Controller
                                    name={field.name}
                                    control={control}
                                    defaultValue=""
                                    render={(props) => controlRender(props, field)}
                                />
                            </Field>
                        ))}
                    </FormFields>
                    <Actions>
                        <BackButton
                            variant={ButtonVariant.Outline}
                            type="button"
                            onClick={() => {
                                setCollapsed(false);
                                reset();
                            }}
                        >
                            {t('pages.profile.staff.create.cancel')}
                        </BackButton>
                        <SubmitButton type="submit">
                            {t('pages.profile.staff.create.create')}
                        </SubmitButton>
                    </Actions>
                </form>
            ) : (
                <AddParticipantButton />
            )}
            {IS_NG && <CredentialsFIRSForm />}
        </CustomContentContainer>
    );
};
